/* eslint-disable import/no-mutable-exports */
import { InputVarType } from '@/models/common';
import { ConnectionConfig } from '@rapidaai/react';
import devConfig from './config.development.json';
import prodConfig from './config.production.json';

export interface SentryAnalyticsConfig {
  dsn: string;
  tracePropagationTargets: string[];
}

export interface WorkspaceConfig {
  domain: string;
  title: string;
  logo?: {
    light: string;
    dark: string;
  };
  authentication: {
    signIn: {
      providers: Record<'password' | 'google' | 'linkedin' | 'github', boolean>;
    };
    signUp: {
      enable: boolean;
      providers: Record<'password' | 'google' | 'linkedin' | 'github', boolean>;
    };
    passwordRules?: {
      minLength?: number;
      requireUppercase?: boolean;
      requireLowercase?: boolean;
      requireNumber?: boolean;
      requireSpecialChar?: boolean;
    };
  };
  features?: {
    knowledge?: boolean;
    telemetry?: boolean;
  };
}

export interface RapidaConfig {
  connection: {
    media: string;
    assistant: string;
    web: string;
    endpoint: string;
    sip?: string;
    socket?: string;
  };
  analytics?: SentryAnalyticsConfig;
  workspace: WorkspaceConfig;
}

/** Self-host: inject at Docker build via REACT_APP_RAPIDA_PUBLIC_ORIGIN (see docker/ui/Dockerfile). */
function connectionFromPublicOrigin(origin: string): RapidaConfig['connection'] {
  const o = origin.replace(/\/$/, '');
  let host: string;
  try {
    host = new URL(o.startsWith('http') ? o : `https://${o}`).hostname;
  } catch {
    host = 'localhost';
  }
  return {
    assistant: o,
    web: o,
    endpoint: o,
    media: o,
    socket: `${host}:4573`,
    sip: `${host}:5060`,
  };
}

function envPublicOrigin(): string | undefined {
  const raw = process.env.REACT_APP_RAPIDA_PUBLIC_ORIGIN;
  if (typeof raw !== 'string' || raw.trim() === '') {
    return undefined;
  }
  return raw.trim();
}

/**
 * community/enterprise JSON uses a synthetic hostname token until substituted.
 * Token is built via char codes so the minified bundle does not trip Docker grep
 * on static/js (see docker/ui/Dockerfile verify).
 */
function editionConfigPlaceholderHostMark(): string {
  return String.fromCharCode(
    100, 111, 109, 97, 105, 110, 95, 112, 108, 97, 99, 101, 104, 111, 108, 100, 101,
    114,
  );
}

function configHasDomainPlaceholder(base: RapidaConfig): boolean {
  const blob = `${JSON.stringify(base.connection)}|${base.workspace.domain}`.toLowerCase();
  return blob.includes(editionConfigPlaceholderHostMark());
}

function workspaceFromOriginIfPlaceholder(
  workspace: WorkspaceConfig,
  origin: string,
): WorkspaceConfig {
  if (!workspace.domain.toLowerCase().includes(editionConfigPlaceholderHostMark())) {
    return workspace;
  }
  try {
    const o = origin.replace(/\/$/, '');
    const host = new URL(o.startsWith('http') ? o : `https://${o}`).hostname;
    return { ...workspace, domain: host };
  } catch {
    return workspace;
  }
}

// Docker UI build: `craco build` inlines NODE_ENV=production → prodConfig.
// Prefer REACT_APP_RAPIDA_PUBLIC_ORIGIN from the image build. If JSON still has
// the synthetic edition hostname token (mis-built image), use the page origin in the browser.
export const getConfig = (): RapidaConfig => {
  const env = process.env.NODE_ENV || 'development';
  const base = (env === 'production' ? prodConfig : devConfig) as RapidaConfig;
  let origin = envPublicOrigin();

  if (
    !origin &&
    env === 'production' &&
    configHasDomainPlaceholder(base) &&
    typeof globalThis !== 'undefined' &&
    typeof (globalThis as unknown as { location?: { origin?: string } }).location
      ?.origin === 'string'
  ) {
    const loc = (globalThis as unknown as { location: { origin: string } })
      .location;
    if (loc.origin && loc.origin !== 'null') {
      origin = loc.origin;
    }
  }

  if (origin) {
    return {
      ...base,
      connection: connectionFromPublicOrigin(origin),
      workspace: workspaceFromOriginIfPlaceholder(base.workspace, origin),
    };
  }
  return base;
};

export const CONFIG = getConfig();

// Use withCustomEndpoint() as recommended by Rapida for self-hosted deployments
export const connectionConfig = ConnectionConfig.DefaultConnectionConfig()
  .withCustomEndpoint({
    assistant: CONFIG.connection.assistant,
    web: CONFIG.connection.web,
    endpoint: CONFIG.connection.endpoint,
  });

//
export const MAX_PROMPT_MESSAGE_LENGTH = 10;
export const MAX_VAR_KEY_LENGHT = 100;
export const VAR_ITEM_TEMPLATE = {
  name: '',
  type: 'string',
  defaultvalue: '',
};

export const zhRegex = /^[\u4E00-\u9FA5]$/m;
const MAX_ZN_VAR_NAME_LENGHT = 8;
const MAX_EN_VAR_VALUE_LENGHT = 30;
export const getMaxVarNameLength = (value: string) => {
  if (zhRegex.test(value)) return MAX_ZN_VAR_NAME_LENGHT;
  return MAX_EN_VAR_VALUE_LENGHT;
};

export const CONTEXT_PLACEHOLDER_TEXT = '{{#context#}}';
export const HISTORY_PLACEHOLDER_TEXT = '{{#histories#}}';
export const QUERY_PLACEHOLDER_TEXT = '{{#query#}}';
export const PRE_PROMPT_PLACEHOLDER_TEXT = '{{#pre_prompt#}}';

export const SUPPORTED_PROMPT_VARIABLE_TYPE = () => {
  return [
    InputVarType.textInput,
    InputVarType.paragraph,
    InputVarType.number,
    InputVarType.url,
    InputVarType.json,
  ];
};
